/**
 * Trained Q&A API client — CRUD, bulk import, variant generation, stats.
 */

import { API_BASE, fetchApi, getAuthHeaders } from './apiClient';

// ---- Types ----------------------------------------------------------------

export interface TrainedAnswerCreate {
  title: string;
  answer: string;
  category?: string;
  module?: string;
  variants: string[];
}

export interface TrainedAnswerUpdate {
  title?: string;
  answer?: string;
  category?: string;
  module?: string;
}

export interface VariantResponse {
  id: number;
  question: string;
  created_at: string;
}

export interface TrainedAnswerResponse {
  id: number;
  title: string;
  answer: string;
  category: string | null;
  module: string | null;
  is_active: boolean;
  variants: VariantResponse[];
  created_at: string;
  updated_at: string;
}

export interface TrainedAnswerListResponse {
  answers: TrainedAnswerResponse[];
  total: number;
}

/** Summary row for paginated list — variant_count instead of full variant data. */
export interface TrainedAnswerSummary {
  id: number;
  title: string;
  answer: string;
  category: string | null;
  module: string | null;
  is_active: boolean;
  variant_count: number;
  created_at: string;
  updated_at: string;
}

export interface TrainedAnswerPaginatedResponse {
  answers: TrainedAnswerSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface BulkImportResult {
  total_in_file: number;
  imported: number;
  skipped_truncated: number;
  skipped_max_steps: number;
  skipped_duplicate: number;
  failed: number;
  errors: string[];
}

export interface TrainedQaStats {
  total_answers: number;
  active_answers: number;
  total_variants: number;
  variants_with_embedding: number;
  avg_variants_per_answer: number;
  top_modules: { module: string; count: number }[];
}

// ---- API ------------------------------------------------------------------

export const trainedQaApi = {
  async list(module?: string, activeOnly = true): Promise<TrainedAnswerListResponse> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (module) params.set('module', module);
    params.set('active_only', String(activeOnly));
    const qs = params.toString();
    const res = await fetchApi(`${API_BASE}/trained-qa?${qs}`, { headers });
    if (!res.ok) throw new Error(`List failed: ${res.status}`);
    return res.json();
  },

  /** Server-side paginated list — single query, no N+1, returns variant counts. */
  async listPaginated(opts: {
    page?: number;
    pageSize?: number;
    search?: string;
    module?: string;
    activeOnly?: boolean;
    sortField?: string;
    sortDir?: string;
  } = {}): Promise<TrainedAnswerPaginatedResponse> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    params.set('page', String(opts.page ?? 0));
    params.set('page_size', String(opts.pageSize ?? 20));
    params.set('active_only', String(opts.activeOnly ?? true));
    if (opts.search) params.set('search', opts.search);
    if (opts.module) params.set('module', opts.module);
    if (opts.sortField) params.set('sort_field', opts.sortField);
    if (opts.sortDir) params.set('sort_dir', opts.sortDir);
    const qs = params.toString();
    const res = await fetchApi(`${API_BASE}/trained-qa?${qs}`, { headers });
    if (!res.ok) throw new Error(`List failed: ${res.status}`);
    return res.json();
  },

  async get(id: number): Promise<TrainedAnswerResponse> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/${id}`, { headers });
    if (!res.ok) throw new Error(`Get failed: ${res.status}`);
    return res.json();
  },

  async create(data: TrainedAnswerCreate): Promise<TrainedAnswerResponse> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Create failed: ${res.status}`);
    return res.json();
  },

  async update(id: number, data: TrainedAnswerUpdate): Promise<TrainedAnswerResponse> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    return res.json();
  },

  async remove(id: number): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  },

  async addVariants(answerId: number, variants: string[]): Promise<TrainedAnswerResponse> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/${answerId}/variants`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ variants }),
    });
    if (!res.ok) throw new Error(`Add variants failed: ${res.status}`);
    return res.json();
  },

  async deleteVariant(variantId: number): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/variants/${variantId}`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(`Delete variant failed: ${res.status}`);
  },

  async bulkImport(
    entries: { id: string; question: string; answer: string; module?: string; tags?: string[] }[],
    maxSteps?: number,
    skipTruncated = false,
  ): Promise<BulkImportResult> {
    const headers = await getAuthHeaders();
    const body: any = { entries, skip_truncated: skipTruncated };
    if (maxSteps !== undefined) body.max_steps = maxSteps;
    const res = await fetchApi(`${API_BASE}/trained-qa/import`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Import failed: ${res.status}`);
    return res.json();
  },

  async generateVariants(
    variantsPerQuestion = 3,
    maxExistingVariants = 2,
  ): Promise<{ status: string; job_id?: string; total_answers: number; message: string }> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/generate-variants`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        variants_per_question: variantsPerQuestion,
        max_existing_variants: maxExistingVariants,
      }),
    });
    if (!res.ok) throw new Error(`Generate variants failed: ${res.status}`);
    return res.json();
  },

  /** Poll a background job until it completes or fails (reuses the shared job registry). */
  async pollJobStatus(
    jobId: string,
    intervalMs = 2000,
    maxAttempts = 120,
    onProgress?: (job: { status: string; total: number; completed: number; failed: number }) => void,
  ): Promise<{
    status: string;
    total: number;
    completed: number;
    failed: number;
    errors: string[];
  }> {
    const headers = await getAuthHeaders();
    for (let i = 0; i < maxAttempts; i++) {
      const response = await fetchApi(`${API_BASE}/doc-processing/jobs/${encodeURIComponent(jobId)}`, {
        headers,
      });
      if (!response.ok) throw new Error(`Job status check failed: ${response.status}`);
      const job = await response.json();
      if (onProgress) onProgress(job);
      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('Job polling timed out');
  },

  async wipeAll(): Promise<{ rows_deleted: number }> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/all`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(`Wipe failed: ${res.status}`);
    return res.json();
  },

  async stats(): Promise<TrainedQaStats> {
    const headers = await getAuthHeaders();
    const res = await fetchApi(`${API_BASE}/trained-qa/stats`, { headers });
    if (!res.ok) throw new Error(`Stats failed: ${res.status}`);
    return res.json();
  },
};
